/**
 * Right panel form for the Details step.
 * Uses the new FormBuilder from @kula-ai/components with a config-driven schema.
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormBuilder, type FormBuilderHandle } from '@kula-ai/components';
import type { ScheduleMode, ApiReference } from '../../config/types';
import { useNoteTakerMandateHeal } from './hooks/useNoteTakerMandateHeal';
import { isAdhocMode, isSelfScheduleMode } from '../../config/modeClassifiers';
import { useScheduleCalendarData } from './hooks/useScheduleCalendarData';
import { useDetailsFormSchema } from './hooks/useDetailsFormSchema';
import { useDetailsFormDefaults } from './hooks/useDetailsFormDefaults';
import { useMeetingRoomGroupedOptions } from './hooks/useMeetingRoomGroupedOptions';
import { useShowToast } from '@/hooks/useShowToast';
import { SCHEDULE_ERROR_COPY } from '../../config/errorCopy';
import {
  InterviewerTimezoneModal,
  type InterviewerWithoutTimezone,
} from './timezoneModal';
import { setCachedInterviewerTimezone } from '../../state/interviewerOptionCache';

interface DetailsFormProps {
  mode: ScheduleMode;
  apiReference: ApiReference;
  /** Shared payload ref — used to read pre-hydrated form defaults. */
  payloadRef: React.MutableRefObject<
    import('../../config/types').ScheduleInterviewPayload
  >;
  interviewPlan?: Record<string, unknown>;
  /** Adhoc-only fresh note-taker mandate. Non-adhoc reads it off
   *  `interviewPlan`; adhoc has `interviewPlan` undefined and passes the
   *  plan's `note_taker_mandatory` here so `useNoteTakerConfig` locks the
   *  toggle instead of falling back to the stale `currentJob` slice
   *  (ENG-3339). */
  noteTakerMandatory?: boolean;
  /** Account-level meeting-rooms gate, resolved upstream for every mode
   *  (incl. adhoc, where `interviewPlan` is intentionally undefined).
   *  Drives the "Book a meeting room" checkbox + room-availability fetch. */
  meetingRoomsEnabled?: boolean;
  /** Adhoc-only signal that the wizard was launched as a platform-
   *  specific interview (today: HackerRank). Locks `location` to that
   *  platform and seeds the picker default. */
  interviewPlatform?: 'hackerrank';
  /** ISO start from calendar booked slot — keeps date picker minDate in sync when setValue bypasses onFieldChange */
  calendarBookedStartIso?: string | null;
  /** ISO end from calendar booked slot — used by the meeting-room
   *  availability fetch so rooms are scored against the actual slot. */
  calendarBookedEndIso?: string | null;
  /** ISO start of the week the user is currently viewing on the
   *  calendar. Drives the interviewer workload query's `week_start_date`
   *  param and the dropdown's "Interview load (<range>)" header so
   *  both refresh when the user navigates between weeks. */
  visibleWeekStartIso?: string | null;
  /** Interviewer IDs whose calendar is disconnected — each selected
   *  chip renders a hover-icon warning when its id appears here. */
  disconnectedInterviewerIds?: number[];
  /** Schedule TZ from calendar (must match useFormCalendarSync / ManualCalendar) */
  scheduleTimezone?: string;
  /** Candidate display name for the phone-interview read-only block. */
  candidateName?: string;
  /** Candidate avatar URL for the phone-interview read-only block. */
  candidateAvatar?: string;
  /** Pre-formatted candidate phone for phone_interview location. */
  candidatePhone?: string;
  /** Seed for the FormBuilder phone input on edit. */
  candidatePhoneValue?: {
    countryCode: string;
    dialCode: string;
    number: string;
  };
  /** For the AI-recommended interviewer call. */
  atsJobStageActionId?: number;
  /**
   * `true` once the user has picked at least one interviewer for this
   * interview. The schema reads it to switch off the AI-recommended-
   * interviewer query — without this the query refetches every time
   * the booked slot moves (drag, resize) even though the recommendation
   * pill is already hidden.
   */
  hasInterviewerSelected?: boolean;
  /** Currently-picked interviewer options, for the Host dropdown only.
   *  Pool/plan defaults stay in `participantDefaultOptions`. */
  hostParticipantOptions?: import('./hooks/useDetailsFormSchema').UseDetailsFormSchemaOptions['hostParticipantOptions'];
  /** Recruiter (or, in edit mode, the original interview creator). Always
   *  added to the Host options when not already an interviewer; their
   *  `zoomStatus` drives the chip + the legacy auto-selection. */
  currentUserHostInfo?: import('./hooks/useDetailsFormSchema').UseDetailsFormSchemaOptions['currentUserHostInfo'];
  /** The REAL logged-in user id (distinct from `currentUserHostInfo`,
   *  which is the interview creator in edit mode). Used for "you"
   *  semantics like hiding the set-timezone globe on your own chip. */
  loggedInUserId?: import('./hooks/useDetailsFormSchema').UseDetailsFormSchemaOptions['loggedInUserId'];
  /** Edit mode: the host saved on the existing interview, when it isn't
   *  in the picked interviewer list and isn't the current user. */
  editSavedHost?: import('./hooks/useDetailsFormSchema').UseDetailsFormSchemaOptions['editSavedHost'];
  onFieldChange?: (
    _name: string,
    _value: unknown,
    _allValues: Record<string, unknown>
  ) => void;
  /** Called when the room-name-by-email map updates so the calendar
   *  can render friendly room labels on busy events. */
  onMeetingRoomNamesChange?: (_namesByEmail: Record<string, string>) => void;
  /**
   * Find Time props bag — only passed in self-schedule / self-
   * schedule-adhoc modes. The schema renders a custom row under
   * the Duration field that mounts `<FindTimeField>` with these
   * props; the row hides itself while `participant_ids` is empty.
   */
  findTimeProps?: import('./hooks/useDetailsFormSchema').FindTimeFieldBag;
  /**
   * Buffer props bag — only passed when `feInterviewBuffer` is on and
   * the mode is self-schedule. Drives the `BufferField` mounted
   * under the Duration row.
   */
  bufferProps?: import('./hooks/useDetailsFormSchema').BufferFieldBag;
  /**
   * Lets the parent step open the set-timezone modal imperatively —
   * used by `DetailsStep.validateOrBlock` to pre-populate the modal
   * with every interviewer missing a timezone when the recruiter
   * clicks Next on self-schedule modes (legacy parity with
   * `SchInterviewerTimezoneModal` opened from the Next handler).
   */
  tzModalRef?: React.MutableRefObject<{
    open: (_interviewers: InterviewerWithoutTimezone[]) => void;
  } | null>;
}

export const DetailsForm = memo(
  forwardRef<FormBuilderHandle, DetailsFormProps>(
    (
      {
        mode,
        apiReference,
        payloadRef,
        interviewPlan,
        noteTakerMandatory,
        meetingRoomsEnabled = false,
        interviewPlatform,
        calendarBookedStartIso,
        calendarBookedEndIso,
        visibleWeekStartIso,
        disconnectedInterviewerIds,
        scheduleTimezone,
        candidateName,
        candidateAvatar,
        candidatePhone,
        candidatePhoneValue,
        atsJobStageActionId,
        hasInterviewerSelected,
        hostParticipantOptions,
        currentUserHostInfo,
        loggedInUserId,
        editSavedHost,
        onFieldChange,
        onMeetingRoomNamesChange,
        findTimeProps,
        bufferProps,
        tzModalRef,
      },
      ref
    ) => {
      const internalRef = useRef<FormBuilderHandle>(null);

      const setRefs = useCallback(
        (node: FormBuilderHandle | null) => {
          internalRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref)
            (ref as React.MutableRefObject<FormBuilderHandle | null>).current =
              node;
        },
        [ref]
      );

      const defaultInterviewType =
        (interviewPlan?.kind as string) ??
        (interviewPlan?.interview_kind as string) ??
        'one_on_one';

      const [interviewType, setInterviewType] = useState(defaultInterviewType);
      // Track startDate and startTime for dynamic time option filtering
      const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(
        null
      );
      const [selectedStartTime, setSelectedStartTime] = useState('');
      const [selectedEndTime, setSelectedEndTime] = useState('');
      // Live participant_ids — the note-taker tile is gated on it, and
      // FormBuilder restores its pre-hide snapshot when the tile reappears,
      // so the mandate heal must re-assert on this too.
      const [selectedParticipantIds, setSelectedParticipantIds] = useState(() =>
        JSON.stringify(
          (payloadRef.current.details as Record<string, unknown>)
            ?.participant_ids ?? ''
        )
      );
      // Live `location` mirrored from the form-level onChange (fires on
      // programmatic `setValue` too — unlike `sharedState.location`). Drives
      // the note-taker mandate self-heal below. Seeded from the hydrated
      // default so the heal has the mount location before any change fires.
      const [selectedLocation, setSelectedLocation] = useState(() =>
        String(
          (payloadRef.current.details as Record<string, unknown>)?.location ??
            ''
        )
      );
      // Live calendar_info — drives the Outlook organizer hint + the
      // interviewer "calendar owner" triangle through the schema's
      // useScheduleCalendarData. Tracked from the form-level onChange
      // (not field onFieldChange) so calendar-picker setValue is seen.
      const [selectedCalendarInfo, setSelectedCalendarInfo] = useState<{
        provider_calendar_id?: string;
        is_primary?: boolean;
      } | null>(null);

      // Live "Book a meeting room" checkbox — gates the room-availability
      // fetch so it (and any reconnect-error toast) only fires once the user
      // opts into booking a room, matching the picker's own `book_meeting_room`
      // condition. Seeded from the hydrated default so an edit that already
      // booked a room fetches on the first render. (ENG-3246)
      const [bookMeetingRoom, setBookMeetingRoom] = useState<boolean>(() =>
        Boolean(
          (payloadRef.current.details as Record<string, unknown>)
            ?.book_meeting_room
        )
      );

      // Rooms this interview already booked + its original slot — used to
      // unmask the BE "busy" that's caused by the interview's own booking
      // during a reschedule (see `useMeetingRoomGroupedOptions`).
      const selfBookedRoomEmails = useMemo(
        () =>
          (
            (interviewPlan?.meeting_rooms as
              | Array<{ email?: string }>
              | undefined) ?? []
          )
            .map((r) => r?.email)
            .filter((e): e is string => typeof e === 'string' && e.length > 0),
        [interviewPlan]
      );

      // Meeting-room options grouped by office with availability tags.
      const {
        groups: meetingRoomGroups,
        namesByEmail: meetingRoomNamesByEmail,
        isLoading: meetingRoomsLoading,
        error: meetingRoomsError,
      } = useMeetingRoomGroupedOptions({
        timezone:
          scheduleTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        slotStartIso: calendarBookedStartIso ?? null,
        slotEndIso: calendarBookedEndIso ?? null,
        enabled: meetingRoomsEnabled && bookMeetingRoom,
        isEditReschedule: apiReference === 'edit',
        selfBookedRoomEmails,
        originalSlotStartIso:
          (interviewPlan?.start_time as string | undefined) ?? null,
        originalSlotEndIso:
          (interviewPlan?.end_time as string | undefined) ?? null,
      });

      // Publish the room-name map upwards so the calendar can swap
      // raw meeting subjects for room labels on busy events.
      useEffect(() => {
        onMeetingRoomNamesChange?.(meetingRoomNamesByEmail);
      }, [meetingRoomNamesByEmail, onMeetingRoomNamesChange]);

      // Surface a room-fetch failure (otherwise dropped) as a toast with
      // reconnect-aware copy. The hook puts the BE code on `error.message`
      // (trigger 422 or poll envelope `errors[0]`); map it, falling back to a
      // generic line so an unmapped/blank code never leaks a raw `err_*`.
      const showToast = useShowToast();
      const lastRoomErrorRef = useRef<Error | null>(null);
      useEffect(() => {
        if (
          !meetingRoomsError ||
          lastRoomErrorRef.current === meetingRoomsError
        )
          return;
        lastRoomErrorRef.current = meetingRoomsError;
        const code = meetingRoomsError.message;
        showToast({
          title:
            (code && SCHEDULE_ERROR_COPY[code]) ||
            "Couldn't load meeting rooms. Please try again.",
          status: 'error',
          id: 'meeting-rooms-fetch-error',
        });
      }, [meetingRoomsError, showToast]);

      const defaultValues = useDetailsFormDefaults(payloadRef, mode, {
        candidatePhoneValue,
        interviewPlatform,
      });

      // Live `host_id` mirrored from the form-level onChange (same
      // pattern as `selectedCalendarInfo`) so the schema can gate the
      // `editSavedHost` dropdown option to "only when it IS the current
      // selection" (legacy `SchZoomHost` parity). Seeded from the
      // hydrated default so the saved host renders on the first edit
      // render before any onChange fires; once SCH-71 clears a removed
      // host this mirror empties and the schema drops it from the dropdown.
      const [selectedHostId, setSelectedHostId] = useState<string>(() =>
        String((defaultValues as Record<string, unknown>).host_id ?? '')
      );

      // Bumped after a timezone update so the chip's clock refreshes.
      // Read participant_default_options live from `payloadRef` rather
      // than the frozen `defaultValues` snapshot.
      const [tzVersion, setTzVersion] = useState(0);
      const participantDefaultOptions = useMemo(() => {
        const raw = (payloadRef.current.details as Record<string, unknown>)
          ?.participant_default_options;
        return Array.isArray(raw)
          ? (raw as Array<{
              value: string;
              label: string;
              description?: string;
              image?: string;
              data?: { timezone?: string };
            }>)
          : (defaultValues.participant_default_options as
              | Array<{
                  value: string;
                  label: string;
                  description?: string;
                  image?: string;
                  data?: { timezone?: string };
                }>
              | undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [tzVersion, defaultValues.participant_default_options]);

      // ── Set-timezone modal ────────────────────────────────────────
      // List drives single-vs-multi UI inside the modal. Per-chip globe
      // opens it with one entry; `validateOrBlock` in DetailsStep opens
      // it with N when the recruiter hits Next on a self-schedule mode
      // with missing timezones (legacy `SchInterviewerTimezoneModal`
      // parity — same modal, single or multi by count).
      const [tzModalInterviewers, setTzModalInterviewers] = useState<
        InterviewerWithoutTimezone[]
      >([]);

      const handleSetTimezoneClick = useCallback(
        (it: { id: number; name: string; avatar?: string }) => {
          setTzModalInterviewers([it]);
        },
        []
      );

      // Imperative controller so the step's Next-validator can open the
      // modal pre-populated with every interviewer missing a timezone.
      // Set in an effect (avoids running on render) and cleared on
      // unmount so a stale ref can't fire after the form is gone.
      useEffect(() => {
        if (!tzModalRef) return;
        tzModalRef.current = {
          open: (its: InterviewerWithoutTimezone[]) =>
            setTzModalInterviewers(its),
        };
        return () => {
          tzModalRef.current = null;
        };
      }, [tzModalRef]);

      const handleTimezoneUpdated = useCallback(
        (updates: Array<{ id: number; time_zone: string }>) => {
          const currentDetails = payloadRef.current.details as Record<
            string,
            unknown
          >;
          const currentOpts =
            (currentDetails?.participant_default_options as Array<{
              value: string;
              label: string;
              image?: string;
              description?: string;
              data?: { timezone?: string };
            }>) ?? [];
          const updated = currentOpts.map((o) => {
            const u = updates.find((x) => String(x.id) === String(o.value));
            return u
              ? {
                  ...o,
                  data: { ...(o.data ?? {}), timezone: u.time_zone },
                }
              : o;
          });
          payloadRef.current.details = {
            ...currentDetails,
            participant_default_options: updated,
          };
          // Keep the module-level option cache in sync so the next
          // save-time validator (which prefers the cache for picks the
          // user added via AsyncSelect mid-session) sees the new
          // timezone instead of the stale empty value.
          for (const u of updates) {
            setCachedInterviewerTimezone(String(u.id), u.time_zone);
          }
          setTzVersion((v) => v + 1);
          // No invalidate here: `applyInterviewerTimezoneUpdates` (called by the
          // modal) already patched the cached interviewer lists in place and
          // woke the chips. Invalidating could never have refetched anyway —
          // those entries have no active observers, and `invalidateQueries`
          // defaults to `refetchType: 'active'`.
        },
        [payloadRef]
      );

      // Called when the user selects from the recommended-interviewer pill.
      // `setValue` alone doesn't fire onChange, so we manually push the
      // notification so useFormCalendarSync can update interviewerIds.
      const handleInterviewerRecommend = useCallback(
        (id: string) => {
          const allValues =
            (internalRef.current?.getValues() as Record<string, unknown>) ?? {};
          onFieldChange?.('participant_ids', id, {
            ...allValues,
            participant_ids: id,
          });
        },
        [onFieldChange]
      );

      const schema = useDetailsFormSchema({
        mode,
        apiReference,
        interviewPlan,
        noteTakerMandatory,
        interviewPlatform,
        meetingRoomsEnabled,
        interviewType,
        selectedStartDate,
        selectedStartTime,
        selectedEndTime,
        calendarInfo: selectedCalendarInfo,
        scheduleTimezone,
        calendarBookedStartIso,
        calendarBookedEndIso,
        visibleWeekStartIso,
        disconnectedInterviewerIds,
        meetingRoomGroups,
        meetingRoomsLoading,
        candidateName,
        candidateAvatar,
        candidatePhone,
        atsJobStageActionId,
        hasInterviewerSelected,
        participantDefaultOptions,
        hostParticipantOptions,
        currentUserHostInfo,
        loggedInUserId,
        editSavedHost,
        currentHostId: selectedHostId,
        findTimeProps,
        bufferProps,
        // Route custom date-row edits through the same slot sync the
        // native date fields used.
        onFieldChange,
        onInterviewerRecommend: handleInterviewerRecommend,
        onSetTimezone: handleSetTimezoneClick,
      });

      // `useLocationFieldConfig` resolves `defaultLocation` async
      // (after the availability API returns). FormBuilder freezes
      // `defaultValues` at mount, so we imperatively push the resolved
      // default into the form once — only when the field is still
      // empty, to avoid clobbering a user choice. Edit mode is
      // excluded entirely: the existing interview's location is the
      // source of truth and we must not overwrite it while it hydrates.
      useEffect(() => {
        if (apiReference === 'edit') return;
        const desired = defaultValues.location;
        if (!desired) return;
        const current = internalRef.current?.getValues?.().location;
        if (!current) {
          internalRef.current?.setValue('location', desired);
        }
      }, [apiReference, defaultValues.location]);

      // Auto-seed the shared-calendar value (create flows only).
      // Two cases:
      //   1. Adhoc: form starts with `calendar_info: null` — seed the
      //      account's `is_default || is_primary` calendar so the row
      //      isn't empty.
      //   2. From-plan: plan hydrated a `calendar_info` ID. If the
      //      scheduler doesn't have access to it (id not in
      //      `selectOptions`), fall back to their own default.
      // Edit mode is skipped: the saved interview's calendar wins.
      // Effect runs once the shared-calendars query resolves
      // (`isLoading` flips false) so we don't act on a stale empty list.
      const isAdhocFlow = isAdhocMode(mode);
      const isSelfScheduleFlow = isSelfScheduleMode(mode);
      const {
        isLoading: calendarSeedLoading,
        defaultCalendar: calendarSeedDefault,
        findById: calendarSeedFindById,
        // SCH-70 — fed the LIVE `selectedCalendarInfo` so ownership (and
        // therefore the lock) tracks the picked calendar, not just the
        // default. The seed outputs above don't depend on `calendarInfo`,
        // so adding it here is behaviour-neutral for them.
        privateLockReason: privateToggleLockReason,
      } = useScheduleCalendarData({
        interviewPlan,
        apiReference,
        calendarInfo: selectedCalendarInfo,
        isAdhoc: isAdhocFlow,
        isSelfSchedule: isSelfScheduleFlow,
      });
      useEffect(() => {
        if (apiReference === 'edit') return;
        if (calendarSeedLoading) return;
        const defaultId = calendarSeedDefault?.provider_calendar_id;
        if (!calendarSeedDefault || !defaultId) return;
        const vals = internalRef.current?.getValues?.() as
          | Record<string, unknown>
          | undefined;
        const currentInfo = vals?.calendar_info as
          | { provider_calendar_id?: string }
          | null
          | undefined;
        const currentId = currentInfo?.provider_calendar_id;
        // Adhoc: empty → seed default.
        // From-plan: id set but scheduler lacks access → fall back.
        const needsSeed =
          !currentId || (!isAdhocFlow && !calendarSeedFindById(currentId));
        if (!needsSeed) return;
        const nextInfo = {
          provider_calendar_id: defaultId,
          is_primary: Boolean(calendarSeedDefault.is_primary),
        };
        internalRef.current?.setValue('calendar_info', nextInfo);
        internalRef.current?.setValue('calendar_info_picker', defaultId);
      }, [
        apiReference,
        calendarSeedLoading,
        calendarSeedDefault,
        calendarSeedFindById,
        isAdhocFlow,
      ]);

      // SCH-70 — force the Private toggle OFF (and keep it off) whenever
      // Outlook can't honour a private event: self-schedule, or a shared
      // calendar the recruiter doesn't own. The schema also `disabled`s
      // the switch so the user can't re-enable it; this effect handles the
      // value the user can't reach — the plan's `calendar_visibility:
      // 'private'` default and a live calendar swap that flips ownership.
      // Re-asserts when the lock resolves (shared-calendar list load) or
      // the picked calendar changes. Only writes when currently `true`, so
      // it never clobbers an already-off value or fights the user.
      useEffect(() => {
        if (!privateToggleLockReason) return;
        const vals = internalRef.current?.getValues?.() as
          | Record<string, unknown>
          | undefined;
        if (vals?.calendar_visibility === true) {
          internalRef.current?.setValue('calendar_visibility', false);
        }
      }, [privateToggleLockReason]);

      // `candidatePhoneValue` is derived from `applicationData.contact`,
      // which may resolve AFTER the wizard mounts. FormBuilder froze
      // the defaults at mount, so without this effect the phone field
      // stays empty and `isCandidatePhoneEditing` stays at its initial
      // 'edit' (the "no phone → edit mode" default). Push both once
      // the phone is known, but only when the form still has an empty
      // number — never clobber a value the user already typed.
      useEffect(() => {
        const desiredPhone = defaultValues.candidate_phone;
        if (!desiredPhone?.number) return;
        const vals = internalRef.current?.getValues?.() as
          | Record<string, unknown>
          | undefined;
        const currentPhone = vals?.candidate_phone as
          | { number?: string }
          | undefined;
        if (currentPhone?.number) return;
        internalRef.current?.setValue('candidate_phone', desiredPhone);
        internalRef.current?.setValue('isCandidatePhoneEditing', 'view');
      }, [defaultValues.candidate_phone]);

      // Note-taker mandate self-heal — keeps `enable_note_taker` on whenever
      // the toggle is mandated (disabled + required) for a virtual location,
      // so the Details step can't dead-end on "This field is required" for a
      // switch the user can't reach. Same mandate expression the schema uses
      // for `required`/`disabled`, keyed on the LIVE location so it survives
      // the adhoc/auto-seed race the `sharedState.location` recovery misses.
      const isNoteTakerMandatory = interviewPlan
        ? Boolean(interviewPlan.note_taker_mandatory)
        : Boolean(noteTakerMandatory);
      useNoteTakerMandateHeal({
        formRef: internalRef,
        isMandatory: isNoteTakerMandatory,
        location: selectedLocation,
        participantIds: selectedParticipantIds,
        payloadRef,
      });

      const handleFieldChange = useCallback(
        (name: string, value: unknown, allValues: Record<string, unknown>) => {
          if (name === 'interview_type') {
            const newType = value as string;
            setInterviewType(newType);

            const current = allValues.participant_ids;
            // RHF's `setValue` doesn't fire the field's own `onChange`,
            // so `onFieldChange('participant_ids', ...)` wouldn't reach
            // `useFormCalendarSync` and the calendar would keep fetching
            // events for the dropped interviewer(s). Mirror the
            // downstream notification manually so sharedState stays in
            // sync with the coerced value.
            // Self-schedule keeps the array shape for one_on_one too
            // (each candidate picks one slot owned by one interviewer);
            // only manual/adhoc one_on_one is single-interviewer.
            if (newType === 'one_on_one' && !isSelfScheduleMode(mode)) {
              if (Array.isArray(current) && current.length > 0) {
                const next = current[0];
                internalRef.current?.setValue('participant_ids', next);
                const merged =
                  (internalRef.current?.getValues() as
                    | Record<string, unknown>
                    | undefined) ?? allValues;
                onFieldChange?.('participant_ids', next, merged);
              }
            }
            if (newType === 'panel') {
              if (current && !Array.isArray(current)) {
                const next = [current];
                internalRef.current?.setValue('participant_ids', next);
                const merged =
                  (internalRef.current?.getValues() as
                    | Record<string, unknown>
                    | undefined) ?? allValues;
                onFieldChange?.('participant_ids', next, merged);
              }
            }
          }

          // Host is conditional on `location==='zoom'` + required. A
          // "Host is required" error raised while Zoom was selected is
          // not auto-cleared by RHF when the field is conditionally
          // hidden, so a manual Zoom→other switch would otherwise leave
          // a stale blocking error (same root cause as the Zoom
          // auto-fallback in DetailsStep). Re-validate host_id so the
          // now-inapplicable error clears.
          if (name === 'location' && value !== 'zoom') {
            // '' fails the hidden field's union([undefined, min(1)]);
            // reset to undefined so the z.undefined() branch passes,
            // then re-validate just host_id (not the whole form).
            internalRef.current?.setValue('host_id', undefined);
            void internalRef.current?.trigger('host_id');
          }

          // Date/time tracking for dynamic option building moved to the
          // form-level `onChange` (handleFormValuesChange) so it reflects
          // the ACTUAL form value from every flow — typing, calendar
          // drag/resize/click, edit-mount hydration, reverse sync —
          // not just user field edits (RHF setValue doesn't fire the
          // field-level onFieldChange). Single source of truth.

          onFieldChange?.(name, value, allValues);

          // Force RHF to re-validate this field on every change.
          // FormBuilder's `mode: 'onBlur'` + RHF's default `reValidateMode:
          // 'onChange'` should already do this once a field has registered an
          // error — but in practice a stale "required" message persists on
          // multiple field types (notably `asyncSelect`) after a failed Next /
          // Submit until the user blurs the field again. Calling `trigger`
          // here is idempotent (a no-op for clean fields) and makes the error
          // banner clear the moment the user enters a valid value.
          void internalRef.current?.trigger(name);
        },
        [mode, onFieldChange]
      );

      // Single source of truth for the time-picker option building.
      // FormBuilder's form-level onChange (FormObserver → RHF useWatch)
      // fires on EVERY value change, including programmatic setValue
      // from the calendar↔form sync and edit-mount hydration — the
      // field-level onFieldChange does not. Guarded so unchanged values
      // don't trigger redundant renders.
      const handleFormValuesChange = useCallback(
        (values: Record<string, unknown>) => {
          const nextDate = (values.startDate as Date | null) ?? null;
          const nextStart = (values.startTime as string) || '';
          const nextEnd = (values.endTime as string) || '';
          setSelectedStartDate((prev) => (prev === nextDate ? prev : nextDate));
          setSelectedStartTime((prev) =>
            prev === nextStart ? prev : nextStart
          );
          setSelectedEndTime((prev) => (prev === nextEnd ? prev : nextEnd));
          const nextCal =
            (values.calendar_info as {
              provider_calendar_id?: string;
              is_primary?: boolean;
            } | null) ?? null;
          setSelectedCalendarInfo((prev) => {
            if (
              prev?.provider_calendar_id === nextCal?.provider_calendar_id &&
              prev?.is_primary === nextCal?.is_primary
            ) {
              return prev;
            }
            return nextCal;
          });
          const nextHostId = String((values.host_id as string) ?? '');
          setSelectedHostId((prev) =>
            prev === nextHostId ? prev : nextHostId
          );
          const nextBookRoom = Boolean(values.book_meeting_room);
          setBookMeetingRoom((prev) =>
            prev === nextBookRoom ? prev : nextBookRoom
          );
          const nextLocation = String((values.location as string) ?? '');
          setSelectedLocation((prev) =>
            prev === nextLocation ? prev : nextLocation
          );
          const nextParticipants = JSON.stringify(values.participant_ids ?? '');
          setSelectedParticipantIds((prev) =>
            prev === nextParticipants ? prev : nextParticipants
          );
        },
        []
      );

      return (
        <>
          <FormBuilder
            ref={setRefs}
            schema={schema as any}
            defaultValues={defaultValues}
            onFieldChange={handleFieldChange}
            onChange={handleFormValuesChange}
            columns={1}
            mode="onBlur"
          />
          <InterviewerTimezoneModal
            isOpen={tzModalInterviewers.length > 0}
            onClose={() => setTzModalInterviewers([])}
            interviewers={tzModalInterviewers}
            onTimezoneUpdated={handleTimezoneUpdated}
          />
        </>
      );
    }
  )
);

DetailsForm.displayName = 'DetailsForm';
